import { useEffect, useState } from "react";

import { businessApi } from "../../../../services/api";
import ScheduleFitNote from "./ScheduleFitNote";

// Report 70 (acceptance gap D): before a weekly schedule is assigned or changed, how the proposed week relates to the
// employee's pay agreement in force on its start date. Advisory only: the schedule can still be saved and pay is never
// changed. The server decides what the reader may see — a pay reader gets the full check, a schedule manager without pay
// permission only a neutral notice (no amount, rate or agreement basis ever reaches this screen for them).

const validWeek = (week) => {
  const days = Object.values(week || {});
  const work = days.filter(Boolean).length;
  return days.length === 7 && work > 0 && work < 7;
};

export default function ProposedScheduleFit({ memberId, week, effectiveFrom }) {
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify([memberId, week, effectiveFrom]);

  useEffect(() => {
    if (!memberId || !effectiveFrom || !validWeek(week)) { setFit(null); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      businessApi.getProposedScheduleFit(memberId, { weekly_schedule: week, effective_from: effectiveFrom }, { signal: controller.signal })
        .then((res) => setFit(res?.fit || null))
        .catch(() => { if (!controller.signal.aborted) setFit(null); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
    // key carries every input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!memberId || !validWeek(week)) return null;
  return (
    <section aria-label="Pay agreement check" className="mb-4 rounded-panel border border-default bg-surface-sunken p-3" data-testid="proposed-schedule-fit">
      <h3 className="text-label text-secondary">Pay agreement check</h3>
      {fit?.neutral ? (
        <p className={`mt-1 text-body-sm ${fit.verdict === "ok" ? "text-secondary" : "text-warning"}`} aria-live="polite">{fit.message}</p>
      ) : (
        <>
          <ScheduleFitNote className="mt-1" fit={fit} loading={loading && !fit} />
          {fit && fit.verdict !== "ok" ? (
            <p className="mt-2 text-meta text-tertiary">Advice only: this does not stop the schedule from being saved, and pay never changes automatically.</p>
          ) : null}
        </>
      )}
    </section>
  );
}
